import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid, MenuItem, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { DATE_FORMATS } from '@callmart/types';
import CustomDatePicker from '../components/UI/CustomDatePicker';
import PercentageLoadingOverlay from '../components/Loaders/PercentageLoadingOverlay';
import Container from '../components/Layout/Container';
import {
    useAlertMessage,
    useAllSlotDaysInMonthAndYear,
    useChannels,
    useCookieLocation,
    useCookieWorktype,
    useCustomStyles,
    useLastLocationDeltaUploadDate,
    useLatestLocationDeltas,
    useLocationsByLoggedInUserRole,
    useSelectedChannel,
    useWorktypes,
} from '../hooks';
import { parseXLSXData } from '../utils/xlsxUtils';
import {
    getAllLocationDeltaByBookingId,
    getLocationDeltaForExcelExport,
    uploadLocationDeltas,
} from '../services/locationDelta';
import CardPopover from '../components/UI/CardPopover';
import LocationDeltasHistory from '../components/LocationDelta/LocationDeltasHistory';
import { useCellPopover } from '../hooks/useCellPopover';
import { integerComparator } from '../components/AgGrid/comparators';
import LocationDeltaEmptyTemplateDownloadButton from '../components/LocationDelta/LocationDeltaEmptyTemplateDownloadButton';
import UserContext from '../store/User/UserContext';
import LocationDeltaCurrentDataDownloadButton from '../components/LocationDelta/LocationDeltaCurrentDataDownloadButton';
import WorktypeSelect from '../components/Selects/WorktypeSelect';
import ExportToExcelButton from '../components/Buttons/ExportToExcelButton';
import {
    handleDatePickerMonthChange,
    parseUploadResponse,
    setCommonStateAfterDataUpload,
    showErrorUploadNotification,
} from '../utils/componentUtils';
import ResponsiveDialog from '../components/UI/Dialogs/ResponsiveDialog';
import { getShortDayNameFromDate, setUploadedMonthAndYearToDate } from '../utils/dateUtils';
import { ChannelOptions } from '../enums/ChannelOptions';
import CustomSelect from '../components/UI/CustomSelect';
import UserRoleLocation from '../components/General/UserRoleLocation';
import LastUpload from '../components/General/LastUpload';
import UploadXlsxFileButton from '../components/Buttons/UploadXlsxFileButton';
import { queryClient } from '../services/queryClient';
import { QueryKey } from '../enums/QueryKey';
import { getSlotTimeColumnDef } from '../utils/agGridUtils';
import TableGridItem from '../components/UI/TableGridItem';
import GridContainer from '../components/Layout/GridContainer';
import { useSocket } from '../store/SocketContext';
import { SocketEvents } from '../enums/SocketEvents';
import dayjs from '@utils/dayjs';

const exportColumnNames = ['Worktype', 'Stadt', 'START_TIME', 'Uhrzeit', 'Standortdelta', 'Meldung', 'Grund', 'Kanal'];

const createColumnDefs = (slotDaysInMonth, t, locale) => {
    const columns = [getSlotTimeColumnDef({ headerName: t('table.dateInterval'), maxWidth: 105 })];

    for (let i = 0; i < slotDaysInMonth.length; i++) {
        const dayName = getShortDayNameFromDate(slotDaysInMonth[i].date, locale);
        columns.push({
            field: `day${dayjs(slotDaysInMonth[i].date).format(DATE_FORMATS.DAY_ONLY)}`,
            headerName: `${dayjs(slotDaysInMonth[i].date).format(DATE_FORMATS.DAY_MONTH)}\n${dayName}`,
            minWidth: 90,
            wrapText: true,
            resizable: true,
            comparator: (valueA, valueB) => {
                return integerComparator({ valueA, valueB });
            },
        });
    }

    return columns;
};

const LocationDeltas = () => {
    const { t, i18n } = useTranslation('allDeltas');
    const socket = useSocket();
    const classes = useCustomStyles();

    const loggedInUser = useContext(UserContext);
    const { dispatchAlert } = useAlertMessage();
    const gridRef = useRef(null);

    const [loading, setLoading] = useState(false);
    const [percentage, setPercentage] = useState(0);
    const [monthForUploading, setMonthForUploading] = useState(dayjs().format(DATE_FORMATS.YEAR_MONTH));
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);
    const [successMonth, setSuccessMonth] = useState('');

    const [selectedDate, setSelectedDate] = useState(dayjs());
    const [locationOptions] = useLocationsByLoggedInUserRole();
    const [selectedLocation, setSelectedLocation] = useCookieLocation(locationOptions);
    const [worktypeOptions] = useWorktypes({ location: selectedLocation });
    const [selectedWorktype, setSelectedWorktype] = useCookieWorktype(worktypeOptions);
    const [channelOptions] = useChannels({ name: [ChannelOptions.INBOUND_VOICE.name, ChannelOptions.CHAT.name] });
    const [selectedChannel, setSelectedChannel] = useSelectedChannel(channelOptions);

    const { mutateAsync: handleUploadLocationDeltas } = useMutation({ mutationFn: uploadLocationDeltas });
    const lastUploadDate = useLastLocationDeltaUploadDate(selectedLocation, selectedWorktype);

    const { showPopover, hoveredCell, hoveredCellHistoryList, popoverOpen, popoverClose } = useCellPopover();

    const { data: slotDaysInMonthAndYear } = useAllSlotDaysInMonthAndYear(selectedDate);
    const columnDefs =
        slotDaysInMonthAndYear?.length > 0 ? createColumnDefs(slotDaysInMonthAndYear, t, i18n.language) : [];
    const { data: tableRows } = useLatestLocationDeltas({
        date: selectedDate,
        location: selectedLocation,
        worktype: selectedWorktype,
        channel: selectedChannel,
    });

    const cellClicked = async (params) => {
        if (params.column.colId.startsWith('day')) {
            const day = params.column.colId.replace('day', '');
            if (params.data[`bookingIdDay${day}`]) {
                const locationDeltasList = await getAllLocationDeltaByBookingId(params.data[`bookingIdDay${day}`]);

                popoverOpen(true, params.event.target, locationDeltasList);
            }
        }
    };

    const uploadFiles = (files, closeDropzoneDialog) => {
        setLoading(true);

        const file = files[0];

        const reader = new FileReader();
        reader.onload = async (e) => {
            const dataToUpload = parseXLSXData(e);

            await handleUploadLocationDeltas({
                dataToUpload,
                monthForUploading,
                setPercentage,
            })
                .then(async (res) => {
                    const resData = parseUploadResponse(res, 'Location delta');
                    const { success, locationForUploading, areNewDeltasCreated, errorMessage } = resData;

                    if (success) {
                        if (areNewDeltasCreated) {
                            setSuccessMonth(monthForUploading);
                            setShowSuccessDialog(true);
                            socket.emit(SocketEvents.uploadedLocationDelta, {
                                monthForUploading,
                                locationForUploading,
                            });
                        } else {
                            dispatchAlert({
                                message: t('upload.noNewDeltas'),
                                type: 'warning',
                            });
                        }
                    } else {
                        showErrorUploadNotification({
                            dispatchAlert,
                            errorMessage,
                            customErrorMessage: t('upload.errorTakingLong'),
                        });
                    }
                })
                .catch(async (error) => {
                    Sentry.captureException(error);
                    showErrorUploadNotification({
                        dispatchAlert,
                        errorMessage: error.message,
                        customErrorMessage: t('upload.errorGeneric'),
                    });
                })
                .finally(async () => {
                    setCommonStateAfterDataUpload(setLoading, setPercentage, closeDropzoneDialog);
                    queryClient.invalidateQueries([
                        QueryKey.LastUploadDate + '-locationDelta',
                        { location: selectedLocation, worktype: selectedWorktype },
                    ]);
                });
        };
        reader.readAsArrayBuffer(file);
    };

    const getExportFileName = () => {
        return `Standortdelta_${dayjs(selectedDate).format(DATE_FORMATS.MONTH_YEAR)}.xlsx`;
    };

    useEffect(() => {
        const onUploadedLocationDelta = ({ monthForUploading, locationForUploading }) => {
            if (locationOptions.length > 0 && selectedWorktype && worktypeOptions.length > 0) {
                const uploadedDate = setUploadedMonthAndYearToDate(selectedDate, monthForUploading);
                setSelectedDate(uploadedDate);

                const uploadedLocationInUserLocations = locationOptions.find(
                    (location) => location.id === locationForUploading
                );
                if (uploadedLocationInUserLocations) {
                    setSelectedLocation(locationForUploading);
                    setSelectedWorktype(worktypeOptions[0].id);
                    setSelectedChannel(channelOptions[0].id);
                }
            }
        };
        socket.on(SocketEvents.uploadedLocationDelta, onUploadedLocationDelta);

        return () => {
            socket.off(SocketEvents.uploadedLocationDelta, onUploadedLocationDelta);
        };
    }, [
        locationOptions,
        worktypeOptions,
        channelOptions,
        selectedWorktype,
        selectedDate,
        setSelectedLocation,
        setSelectedChannel,
        setSelectedWorktype,
        socket,
    ]);

    return (
        <Container>
            <GridContainer sx={loading ? classes.containerOpacity : {}}>
                {loggedInUser && (
                    <UserRoleLocation
                        loggedInUser={loggedInUser}
                        selectedLocation={selectedLocation}
                        locationOptions={locationOptions}
                        onChange={setSelectedLocation}
                    ></UserRoleLocation>
                )}
                <Grid item xs={12} md={4} lg={2} xl={2}>
                    <WorktypeSelect
                        selectedWorktype={selectedWorktype}
                        worktypeOptions={worktypeOptions}
                        onChange={setSelectedWorktype}
                    />
                </Grid>
                <Grid item xs={12} md={4} lg={2} xl={2}>
                    <CustomSelect
                        selectedValue={selectedChannel}
                        options={channelOptions}
                        onChange={setSelectedChannel}
                        label={t('field.channel')}
                    >
                        {channelOptions &&
                            channelOptions.map((channel) => (
                                <MenuItem value={channel.id} key={channel.id}>
                                    <span className="notranslate">{channel.name}</span>
                                </MenuItem>
                            ))}
                    </CustomSelect>
                </Grid>
                <Grid item xs={12} md={4} lg={2}>
                    <CustomDatePicker
                        date={selectedDate}
                        views={['year', 'month']}
                        openTo="month"
                        onChange={(newValue) => handleDatePickerMonthChange(selectedDate, newValue, setSelectedDate)}
                    ></CustomDatePicker>
                </Grid>
                <Grid item xs={9} md={6} lg={2}>
                    <LastUpload showInfoIcon={false} date={lastUploadDate}></LastUpload>
                </Grid>
                {(loggedInUser?.isTEFUser || loggedInUser?.isWFMUser || loggedInUser?.isWTMUser) && (
                    <Grid item xs={3} md={1} lg={1} xl={1}>
                        <ExportToExcelButton
                            columnNames={exportColumnNames}
                            sheetName="Tabelle1"
                            getFileName={getExportFileName}
                            wbTitle="Standortdelta"
                            wbSubject="Export Standortdelta"
                            getDataFromDb={() =>
                                getLocationDeltaForExcelExport(
                                    dayjs(selectedDate).startOf('month').format(DATE_FORMATS.ISO_DATETIME),
                                    dayjs(selectedDate).endOf('month').format(DATE_FORMATS.ISO_DATETIME)
                                )
                            }
                        />
                    </Grid>
                )}

                {(loggedInUser?.isOSPUser || loggedInUser?.isWFMUser) && (
                    <Grid item container xs={12} md={6} lg={6}>
                        <LocationDeltaEmptyTemplateDownloadButton
                            selectedDate={selectedDate}
                            selectedLocation={selectedLocation}
                            allWorktypes={worktypeOptions}
                        />
                        <LocationDeltaCurrentDataDownloadButton
                            date={selectedDate}
                            selectedLocation={selectedLocation}
                            worktypes={worktypeOptions}
                        />
                        <UploadXlsxFileButton
                            buttonName={t('action.upload')}
                            monthForUploading={monthForUploading}
                            onChangeMonthForUploading={setMonthForUploading}
                            uploadFiles={uploadFiles}
                            dialogTitle={t('upload.dialogTitle')}
                        ></UploadXlsxFileButton>
                    </Grid>
                )}
                <TableGridItem
                    ref={gridRef}
                    rowData={tableRows}
                    defaultColDef={{
                        editable: false,
                        filter: true,
                        sortable: true,
                    }}
                    columnDefs={columnDefs}
                    onCellClicked={cellClicked}
                ></TableGridItem>
            </GridContainer>
            <PercentageLoadingOverlay loading={loading} percentage={percentage}></PercentageLoadingOverlay>
            {showPopover && (
                <CardPopover anchorEl={hoveredCell} onClose={popoverClose}>
                    <LocationDeltasHistory locationDeltasHistory={hoveredCellHistoryList} />
                </CardPopover>
            )}
            <ResponsiveDialog
                open={showSuccessDialog}
                onClose={() => setShowSuccessDialog(false)}
                title={t('upload.successTitle')}
            >
                <Typography>
                    {t('upload.successMessage', {
                        month: dayjs(successMonth, DATE_FORMATS.YEAR_MONTH).format(DATE_FORMATS.MONTH_NAME_YEAR),
                    })}
                </Typography>
            </ResponsiveDialog>
        </Container>
    );
};

export default LocationDeltas;
